import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { activeFilterCount, type CatalogFilters } from '@/entities/catalog';
import type { Workspace } from '@/entities/workspace';
import { Icons } from '@/shared/ui';

import styles from './HomeDashboard.module.css';
import { catalogLink, HOME_OFFERED } from './home-filters';
import { SectionHead } from './SectionHead';

/** Workspaces shown on Home before the rest sit behind "All workspaces". */
const PREVIEW = 8;

interface Props {
  personal: Workspace | undefined;
  teams: readonly Workspace[];
  /** The unified search text and filters of the page. */
  query: string;
  filters: CatalogFilters;
  userId: string | null;
  trashCount: number;
}

/**
 * The Workspaces section of Home: the personal workspace first (marked as personal, never as a
 * team), then the team workspaces, as compact resource cards under the same header as Boards and
 * Databases. The page's search and filters apply as far as they mean something for a workspace
 * (type: only "all"; owner; personal / team; the text); a favorites or recently-opened filter has no
 * meaning for workspaces (there is no such mark for them), so it is not applied and a note says so. Having no team workspace is
 * not announced - the personal one simply stands alone.
 */
export function WorkspaceStrip({ personal, teams, query, filters, userId, trashCount }: Props) {
  const { t } = useTranslation();
  const personalLabel = t('home.personalWorkspace');
  if (filters.type !== 'all') return null;
  const needle = query.trim().toLocaleLowerCase();
  const fits = (workspace: Workspace) => {
    const personalOne = workspace.workspace_type === 'personal';
    if (filters.space !== 'any' && filters.space !== (personalOne ? 'personal' : 'team')) {
      return false;
    }
    if (filters.owner !== 'any') {
      const mine = workspace.is_owner && userId !== null;
      if (filters.owner === 'mine' ? !mine : mine) return false;
    }
    return (
      needle === '' ||
      workspace.name.toLocaleLowerCase().includes(needle) ||
      (personalOne && personalLabel.toLocaleLowerCase().includes(needle))
    );
  };
  const all = [...(personal ? [personal] : []), ...teams].filter(fits);
  const shown = all.slice(0, PREVIEW);
  const narrowed = needle !== '' || activeFilterCount(filters, HOME_OFFERED) > 0;
  return (
    <section className={styles.workspaces} aria-labelledby="home-workspaces-title">
      <SectionHead
        id="home-workspaces-title"
        title={t('nav.workspaces')}
        count={all.length}
        to={catalogLink('/app/browse/workspaces', query)}
        linkLabel={t('home.allWorkspaces')}
        linkTestId="all-workspaces-link"
        extra={
          <Link
            to="/app/workspace-trash"
            className={styles.trashLink}
            aria-label={
              trashCount > 0
                ? `${t('home.workspaceTrash')} (${String(trashCount)})`
                : t('home.workspaceTrash')
            }
            title={t('home.workspaceTrash')}
            data-testid="workspace-trash-link"
          >
            <Icons.trash />
            {trashCount > 0 && <span className={styles.count}>{trashCount}</span>}
          </Link>
        }
      />
      {(filters.favorites || filters.recent) && (
        <p className={styles.sectionNote}>{t('home.workspaceFilterNote')}</p>
      )}
      {shown.length === 0 ? (
        <p className={styles.empty}>
          {narrowed ? t('home.noWorkspacesFiltered') : t('home.noWorkspaces')}
        </p>
      ) : (
        <ul className={styles.wsGrid}>
          {shown.map((workspace) => {
            const isPersonal = workspace.workspace_type === 'personal';
            return (
              <li key={workspace.id}>
                <Link
                  to={`/app/workspaces/${workspace.id}`}
                  className={styles.wsTile}
                  data-testid={isPersonal ? 'personal-workspace-link' : 'home-workspace'}
                  title={workspace.description ?? undefined}
                >
                  {isPersonal ? <Icons.user /> : <Icons.workspace />}
                  <span className={styles.wsText}>
                    <span className={styles.wsName}>
                      {isPersonal ? personalLabel : workspace.name}
                    </span>
                    <span className={styles.wsKind}>
                      {isPersonal ? t('home.scopePersonal') : t(`roles.${workspace.role}`)}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
